import { useCallback, useEffect } from "react"
import { useHistory, useLocation } from "react-router-dom"
import routes from "routes"
import { useTypedSelector } from "store"

/**
 * Redirect to the relevant step if some required component isn't selected.
 *
 * For example, if there's no selected color then redirect to the color step.
 *
 * @returns `true` if all required components are selected, otherwise `false`
 */
export default function useRedirectIfPreviousComponentsAreNotSelected() {
  const { initialized } = useTypedSelector((state) => state.wizard)
  const { selectedDetailingCode } = useTypedSelector((state) => state.detailing)
  const { selectedColorCode } = useTypedSelector((state) => state.color)
  const { selectedUpholsteryCode } = useTypedSelector(
    (state) => state.upholstery
  )

  const { pathname } = useLocation()

  const history = useHistory()

  const currentStepIsAfter = useCallback(
    (step: Exclude<keyof typeof routes, "array">) => {
      const currentStepIndex = routes.array.findIndex(
        (r) => r.path === pathname
      )
      return currentStepIndex > routes.array.indexOf(routes[step])
    },
    [pathname]
  )

  useEffect(() => {
    if (!initialized) return

    if (!selectedDetailingCode && currentStepIsAfter("detailing"))
      history.replace(routes.detailing.path)
    else if (!selectedColorCode && currentStepIsAfter("color"))
      history.replace(routes.color.path)
    else if (!selectedUpholsteryCode && currentStepIsAfter("upholstery"))
      history.replace(routes.upholstery.path)
  }, [
    initialized,
    currentStepIsAfter,
    selectedDetailingCode,
    selectedColorCode,
    selectedUpholsteryCode,
    history,
  ])

  return Boolean(
    selectedDetailingCode && selectedColorCode && selectedUpholsteryCode
  )
}
